import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

// Icons (you can replace with react-icons if you prefer)
import { FaTaxi, FaCity, FaPlaneDeparture, FaTruck, FaHotel } from "react-icons/fa";

// Hero image
import aboutHero from "../assets/bus-bg.jpg";
import bus from "../assets/bus.png";

function Services() {
    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    const services = [
        {
            icon: <FaTaxi />,
            title: "Local Taxi Service",
            desc: "Prompt and reliable transportation within the local area.",
        },
        {
            icon: <FaCity />,
            title: "City Tours",
            desc: "Experienced drivers who offer insights into local landmarks.",
        },
        {
            icon: <FaPlaneDeparture />,
            title: "Airport Transfers",
            desc: "Punctuality to ensure on-time arrivals and departures.",
        },
        {
            icon: <FaTruck />,
            title: "Logistics & Freight",
            desc: "Safe and efficient transportation solutions for your goods.",
        },
        {
            icon: <FaHotel />,
            title: "Hotel Booking",
            desc: "Helping you find and book the best value hotels quickly.",
        },
    ];

    return (
        <div className="w-full overflow-hidden">

            {/* ================= HERO SECTION ================= */}
            <section className="relative h-[40vh] w-full">
                {/* Background */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${aboutHero})` }}
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60" />

                {/* Content */}
                <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center text-white">
                    <h1
                        data-aos="fade-up"
                        className="mb-4 text-4xl font-extrabold sm:text-5xl md:text-6xl"
                    >
                        Services
                    </h1>

                    <p
                        data-aos="fade-up"
                        data-aos-delay="150"
                        className="max-w-2xl text-sm text-gray-200 sm:text-base"
                    >
                        Reliable Taxi & Tour Services Across Sri Lanka
                    </p>
                </div>
            </section>

            {/* ================= SERVICES SECTION ================= */}
            <section className="bg-white py-16">
                <div className="mx-auto max-w-6xl px-4 text-center">
                    <p className="mb-2 text-sm font-semibold uppercase text-yellow-500">
                        Services
                    </p>
                    <h2 className="mb-12 text-3xl font-extrabold sm:text-4xl">
                        Fully Professional Services
                    </h2>

                    <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className="flex items-start gap-5 rounded-xl bg-white p-6 shadow-sm transition hover:shadow-lg"
                            >
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-2xl text-black">
                                    {service.icon}
                                </div>

                                <div className="text-left">
                                    <h3 className="mb-2 text-lg font-bold">
                                        {service.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {service.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= FAQ SECTION ================= */}
            <section className="bg-[#111] py-10 text-white">
                <div className="mx-auto max-w-6xl px-4">
                    <h2 className="mb-12 text-center text-4xl font-extrabold">
                        FAQ
                    </h2>

                    <div className="space-y-6">
                        {/* FAQ Item 1 */}
                        <details className="group rounded-lg border border-gray-700 p-6">
                            <summary className="flex cursor-pointer items-center justify-between text-lg font-semibold transition-colors group-open:text-yellow-400">
                                Are your drivers professional and experienced?
                                <span className="transition group-open:rotate-180">⌃</span>
                            </summary>
                            <p className="mt-4 text-gray-300">
                                Absolutely. Our drivers are carefully selected and undergo
                                thorough training. They are experienced, licensed, and
                                committed to providing safe and comfortable rides for our
                                passengers.
                            </p>
                        </details>

                        {/* FAQ Item 2 */}
                        <details className="group rounded-lg border border-gray-700 p-6">
                            <summary className="flex cursor-pointer items-center justify-between text-lg font-semibold transition-colors group-open:text-yellow-400">
                                How do you ensure safety during rides?
                                <span className="transition group-open:rotate-180">⌃</span>
                            </summary>
                            <p className="mt-4 text-gray-300">
                                We maintain our vehicles regularly, follow strict safety
                                protocols, and monitor all rides to ensure maximum passenger
                                safety.
                            </p>
                        </details>

                        {/* FAQ Item 3 */}
                        <details className="group rounded-lg border border-gray-700 p-6">
                            <summary className="flex cursor-pointer items-center justify-between text-lg font-semibold transition-colors group-open:text-yellow-400">
                                Can I request a specific vehicle for my wedding?
                                <span className="transition group-open:rotate-180">⌃</span>
                            </summary>
                            <p className="mt-4 text-gray-300">
                                Yes, you can request specific vehicles for special occasions
                                like weddings. Availability depends on advance booking.
                            </p>
                        </details>

                        {/* FAQ Item 4 */}
                        <details className="group rounded-lg border border-gray-700 p-6">
                            <summary className="flex cursor-pointer items-center justify-between text-lg font-semibold transition-colors group-open:text-yellow-400">
                                Do you offer group transportation options?
                                <span className="transition group-open:rotate-180">⌃</span>
                            </summary>
                            <p className="mt-4 text-gray-300">
                                Yes, we provide group transportation solutions including
                                buses and vans for events, tours, and corporate needs.
                            </p>
                        </details>
                    </div>
                </div>
            </section>

            {/* ================= TAXI CTA SECTION ================= */}
            <section className="w-full bg-[#f7f7f7] py-16">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">

                        {/* LEFT CONTENT */}
                        <div
                            data-aos="fade-right"
                            className="flex flex-col items-center text-center"
                        >
                            <h2 className="mb-4 text-3xl font-extrabold text-black sm:text-4xl">
                                Book your Taxi Today!
                            </h2>

                            <p className="mb-6 max-w-xl text-gray-700">
                                Richie Travel is the best and no.1 Taxi & Tour service provider in Sri
                                Lanka. We always offer you the best Taxi deals covering all islands.
                            </p>

                            <p className="mb-6 text-2xl font-bold text-black">
                                +94 70 515 2858
                            </p>

                            <a
                                href="tel:+94743205692"
                                className="inline-block rounded bg-black px-8 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900"
                            >
                                Contact Us
                            </a>
                        </div>


                        {/* RIGHT IMAGE */}
                        <div
                            data-aos="fade-left"
                            className="flex justify-center md:justify-end"
                        >
                            <img
                                src={bus}
                                alt="Taxi Service"
                                className="max-w-full object-contain"
                            />
                        </div>

                    </div>
                </div>
            </section>

            {/* ================= WHATSAPP BUTTON ================= */}
            <a
                href="https://wa.me/94705152858"
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-5 right-5 z-50"
            >
                <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/512px-WhatsApp.svg.png"
                    alt="WhatsApp"
                    className="h-12 w-12 rounded-full shadow-lg transition hover:scale-110 sm:h-14 sm:w-14"
                />
            </a>

        </div>
    );
}

export default Services;
